import { useCallback, useEffect, useRef } from "react";

import "./SidebarResizeHandle.scss";

export const SIDEBAR_MIN_WIDTH = 240;
export const SIDEBAR_MAX_WIDTH = 500;
// leave at least this much of the window visible as canvas, so the sidebar
// can never swallow the whole viewport on a narrow browser window
const MIN_VISIBLE_CANVAS = 150;

export const clampSidebarWidth = (width: number) => {
  const viewportMax = Math.max(
    SIDEBAR_MIN_WIDTH,
    window.innerWidth - MIN_VISIBLE_CANVAS,
  );
  return Math.min(
    Math.max(width, SIDEBAR_MIN_WIDTH),
    Math.min(SIDEBAR_MAX_WIDTH, viewportMax),
  );
};

type SidebarResizeHandleProps = {
  width: number;
  onWidthChange: (width: number) => void;
};

/**
 * Thin drag handle pinned to the sidebar's own left edge (right edge in
 * RTL). Shared by all three right-panel tabs (Properties / Screens & export
 * / Animation) since they're all tabs of the same underlying sidebar
 * Island — one handle, rendered once in AppSidebar, resizes whichever tab
 * is currently visible.
 */
export const SidebarResizeHandle = ({
  width,
  onWidthChange,
}: SidebarResizeHandleProps) => {
  const dragState = useRef<{ startX: number; startWidth: number } | null>(null);

  const onPointerMove = useCallback(
    (event: PointerEvent) => {
      if (!dragState.current) {
        return;
      }
      const isRTL = document.documentElement.dir === "rtl";
      // dragging away from the canvas (left in LTR, right in RTL) widens
      // the sidebar; dragging toward it narrows the sidebar
      const delta = isRTL
        ? event.clientX - dragState.current.startX
        : dragState.current.startX - event.clientX;
      onWidthChange(clampSidebarWidth(dragState.current.startWidth + delta));
    },
    [onWidthChange],
  );

  const stopDragging = useCallback(() => {
    dragState.current = null;
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", stopDragging);
  }, [onPointerMove]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (event.button !== 0) {
        return;
      }
      event.preventDefault();
      dragState.current = { startX: event.clientX, startWidth: width };
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", stopDragging);
    },
    [width, onPointerMove, stopDragging],
  );

  // safety net: release any lingering global listeners if this unmounts
  // mid-drag (e.g. sidebar closed while resizing)
  useEffect(() => stopDragging, [stopDragging]);

  return (
    <div
      className="sidebar-resize-handle"
      onPointerDown={onPointerDown}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      aria-valuenow={width}
      aria-valuemin={SIDEBAR_MIN_WIDTH}
      aria-valuemax={SIDEBAR_MAX_WIDTH}
    />
  );
};
